import { Title, Group, Grid, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { PageWithBreadcrumbs } from '../../components/layout/PageWithBreadcrumbs';
import { ContainerWhite } from '../../components/core';
import AfaButton from '../../components/ui/AfaButton/AfaButton';
import { AfaCombobox } from '../../components/ui';
import { AfaTable } from '../../components/ui/AfaTable/AfaTable';
import { AfaDatePickerInput } from '../../components/ui/AfaDatePickerInput';
import { AfaMultiSelect } from '../../components/ui/AfaMultiSelect';
import { AfaTextInput } from '../../components/ui/AfaTextInput';
import { useTramitsData } from '../../hooks/useTramitsData';
import { getTramitsColumns } from '../../config/tramitsTableConfig';
import { DeleteTramitModal } from '../../components/Modals/DeleteTramitModal/DeleteTramitModal';
import { CancelTramitModal } from '../../components/Modals/CancelTramitModal/CancelTramitModal';
import { useTramitsFilter } from '../../hooks/useTramitsFilter';
import moment from 'moment';

// Página para consultar el estado de trámites existentes
export function TramitsConsultar(props: any) {
  const navigate = useNavigate();

  const [searchLoading, setSearchLoading] = useState<boolean>(true);
  const { data: allTramitsData, loading, error, refetch } = useTramitsData(props.apiService, props.entity.lookupId, setSearchLoading);
  const { data: filters, loading: filtersLoading } = useTramitsFilter(props.apiService, props.entity.lookupId);

  const [filteredData, setFilteredData] = useState(allTramitsData);

  useEffect(() => {
    setFilteredData(allTramitsData);
  }, [allTramitsData]);

  // Estados para todos los campos del formulario
  const [tipusProces, setTipusProces] = useState<string>('');
  const [proces, setProces] = useState<string>('');
  const [dataDesDe, setDataDesDe] = useState<Date | null>(null);
  const [dataFinsA, setDataFinsA] = useState<Date | null>(null);
  const [filial, setFilial] = useState<string>('');
  const [usuariCreador, setUsuariCreador] = useState<string>('');
  const [estat, setEstat] = useState<string[]>([]);
  const [id, setId] = useState<string>('');
  const [cancelModalOpened, setCancelModalOpened] = useState<boolean>(false);
  const [selectedTramitId, setSelectedTramitId] = useState<string>('');
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pagination = {
  currentPage: currentPage,
  onPageChange: setCurrentPage,
};

  const handleIniciarNou = () => {
    navigate('/tramits/iniciar-nou');
  };

  const handleEsborrarFiltres = () => {
    setTipusProces('');
    setProces('');
    setDataDesDe(null);
    setDataFinsA(null);
    setFilial('');
    setUsuariCreador('');
    setEstat([]);
    setId('');
    setFilteredData(allTramitsData);
  };

  // Acción para aplicar los filtros
  const handleAplicarFiltres = async () => {
    setSearchLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      let filtered = [...allTramitsData];

      // Filtrar por ID
      if (id) {
        filtered = filtered.filter(tramit =>
          tramit.guid.toLowerCase().includes(id.toLowerCase())
        );
      }

      // Filtrar por Estat (puede ser múltiple)
      if (estat.length > 0 && !estat.includes('Tots')) {
        filtered = filtered.filter(tramit =>
          estat.includes(tramit.state)
        );
      }

      // Filtrar por Tipus de tramit
      if (tipusProces && tipusProces !== 'Tots') {
        filtered = filtered.filter(tramit =>
          tramit.processType.toString().toLowerCase().includes(tipusProces.toString().toLowerCase())
        );
      }

      if (proces && proces !== 'Tots') {
        filtered = filtered.filter(tramit =>
          tramit.tramitType.lookupId.toString().toLowerCase().includes(proces.toString().toLowerCase())
        );
      }

      // Filtrar por rango de fechas
      if (dataDesDe || dataFinsA) {
        filtered = filtered.filter(tramit => {
          const tramitDate = moment(tramit.registerDate).format('YYYY-MM-DD');

          const desde = dataDesDe ? moment(dataDesDe).format('YYYY-MM-DD') : null;
          const finsA = dataFinsA ? moment(dataFinsA).format('YYYY-MM-DD') : null;

          if (desde && finsA) {
            return moment(tramitDate).isBetween(desde, finsA, undefined, '[]');
          } else if (desde) {
            return moment(tramitDate).isSameOrAfter(desde);
          } else if (finsA) {
            return moment(tramitDate).isSameOrBefore(finsA);
          }
          return true;
        });
      }

      // Filtrar por Filial
      if (filial && filial !== 'Tots') {
        filtered = filtered.filter(tramit =>
          tramit.filial.lookupValue.toLowerCase().includes(filial.toLowerCase())
        );
      }

      // Filtrar por Usuari creador
      if (usuariCreador && usuariCreador !== 'Tots') {
        filtered = filtered.filter(tramit =>
          tramit.createdBy.lookupValue.toLowerCase().includes(usuariCreador.toLowerCase())
        );
      }
      setFilteredData(filtered);
    } catch (error) {
      console.error('Error en la búsqueda de trameses:', error);
      // En el futuro, aquí podrías manejar errores de API
    } finally {
      setSearchLoading(false);
    }

    setCurrentPage(1); // Reiniciar a la primera página al aplicar filtros nuevos
  };

  // Handlers para la tabla
  const tableHandlers: any = {
    onCancelTramit: (tramitId: string) => {
      setSelectedTramitId(tramitId);
      setCancelModalOpened(true);
    },
    onDeleteDraft: (tramitId: string) => {
      setSelectedTramitId(tramitId);
      setDeleteModalOpened(true);
    }
  };

  // Obtener columnas de la tabla
  const columns = getTramitsColumns(navigate, tableHandlers, props.user);

  // Verificar si hay filtros aplicados
  const hasActiveFilters = useMemo(() => {
    return (
      tipusProces !== '' ||
      proces !== '' ||
      dataDesDe !== null ||
      dataFinsA !== null ||
      filial !== '' ||
      usuariCreador !== '' ||
      estat.length > 0 ||
      id !== ''
    );
  }, [tipusProces, proces, dataDesDe, dataFinsA, filial, usuariCreador, estat, id]);

  return (
    <PageWithBreadcrumbs>
      <div>
        <Title
          order={1}
          mb="sm"
        >
          Consultar Tràmits
        </Title>

        <Group justify="space-between" align="flex-end">
          <Title
            order={3}
          >
            Filtres
          </Title>
          <AfaButton
            id="btn-iniciar-nou"
            leftIcon={IconPlus}
            onClick={handleIniciarNou}
            variant="filled"
          >
            Iniciar nou
          </AfaButton>
        </Group>
      </div>

      {/* Contenedor principal para formularios, tablas, etc. */}
      <ContainerWhite>

        <Grid>
          <Grid.Col span={6}>
            {/* <Grid.Col span={6}> */}
            <AfaTextInput
              label="ID"
              placeholder="Introdueix ID"
              value={id}
              onChange={(e) => setId(e.target.value)}
              name="id"
              id="id-tràmit"
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <AfaTextInput
              label="Creada per"
              placeholder="Creada per"
              value={usuariCreador}
              onChange={(e) => setUsuariCreador(e.target.value)}
              name="usuariCreador"
              id="usuari-creador"
            />
          </Grid.Col>
        </Grid>

        <Grid mt="md">
          <Grid.Col span={6}>
            <AfaCombobox
              data={
                filters.tipusProces != null ?
                ["Tots", ...filters.tipusProces]
              : []}
              label="Tipus de procés"
              placeholder="Tots"
              value={tipusProces}
              onChange={setTipusProces}
              name="tipusProces"
              id="tipus-proces"
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <AfaCombobox
              data={
                filters.process != null ?
                ["Tots", ...filters.process.sort((a, b) => a.label.localeCompare(b.label)) as any]
              : []}
              label="Procés"
              placeholder="Tots"
              value={proces}
              onChange={setProces}
              name="proces"
              id="proces"
            />
          </Grid.Col>
        </Grid>

        {/* Segunda línea: Fecha de presentación desde, Hasta, Filial */}
        <Grid mt="md">
          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Data de presentació des de"
              placeholder="Selecciona data"
              value={dataDesDe}
              onChange={setDataDesDe}
              name="dataDesDe"
              id="data-des-de"
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Fins a"
              placeholder="Selecciona data"
              value={dataFinsA}
              onChange={setDataFinsA}
              name="dataFinsA"
              id="data-fins-a"
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <AfaCombobox
              data={['Tots', ...filters.filial]}
              label="Filial"
              placeholder="Totes"
              value={filial}
              onChange={setFilial}
              name="filial"
              id="filial"
            />
          </Grid.Col>
        </Grid>

        {/* Tercera línea: Usuario creador y Estado */}
        <Grid mt="md">
          <Grid.Col span={6}>
            <AfaMultiSelect
              data={[
                'En revisió',
                'Esborrany',
                'Finalitzat',
                'Pendent rebre documentació',
                'Pendent rebre pagament',
                'Cancel·lat entitat',
                'Rebutjat AFA',
              ]}
              label="Estat"
              placeholder="Selecciona estats"
              value={estat}
              onChange={setEstat}
              name="estat"
              id="estat"
            />
          </Grid.Col>
        </Grid>

        {/* Cuarta línea: Botones de acción */}
        <Group justify="flex-end" gap={16} mt="lg">
          <AfaButton
            id="btn-esborrar-filtres"
            variant="outline"
            onClick={handleEsborrarFiltres}
          >
            Esborrar filtres
          </AfaButton>
          <AfaButton
            id="btn-cercar"
            variant="filled"
            onClick={handleAplicarFiltres}
          >
            Cercar
          </AfaButton>
        </Group>

        {/* Contador de resultados */}
        <Group gap={8} mt="md">
          <Text size="xl" fw={600}>{filteredData.length}</Text>
          <Text size="md"> {filteredData.length === 1 ? 'Resultat' : 'Resultats'}</Text>
        </Group>

        {/* Tabla de resultados */}
        <div style={{ marginTop: '12px' }} id="tramits-table-container">
          <AfaTable
            columns={columns}
            data={filteredData}
            loading={searchLoading}
            loadingMessage="Cercant tràmits..."
            emptyState={
              hasActiveFilters
                ? {
                  title: "No hi ha dades per mostrar.",
                  description: "Intenta ajustar els filtres o afegir nous criteris de cerca."
                }
                : {
                  title: "No hi ha dades per mostrar."
                }
            }
            pagination={pagination}
          />
        </div>
      </ContainerWhite>

      {/* Modal de cancelación */}
      <CancelTramitModal
        apiService={props.apiService}
        opened={cancelModalOpened}
        onClose={() => setCancelModalOpened(false)}
        tramitId={selectedTramitId}
      />
      {/* Modal de cancelación */}
      <DeleteTramitModal
        apiService={props.apiService}
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false)
        }}
        tramitId={selectedTramitId}
      />
    </PageWithBreadcrumbs>
  );
}
